import * as React from "react";

import { Button } from "@/components/ui/button";

export interface ConflictFact {
  /** The fact's label as the editor shows it, such as "Preferred name". */
  label: string;
  /** Their saved value, formatted for reading. */
  theirs: string;
}

/**
 * A save met a newer version. The user's entry stays where it is, their
 * version is shown fact by fact, and the user keeps their edits or loads the
 * newer version. It appears only when the conflict happens (object checklist
 * item 5) and is the one notice every guarded editor uses.
 */
export function ConflictNotice({
  object,
  message,
  facts,
  disabled,
  keepMineLabel = "Keep my edits",
  loadTheirsLabel = "Load their version",
  onKeepMine,
  onLoadTheirs,
}: {
  /** What was edited, in the user's words: "this plan", "this person". */
  object: string;
  /** The lead sentence, when "Someone saved a newer version of ..." does not fit. */
  message?: string;
  facts: ConflictFact[];
  disabled?: boolean;
  keepMineLabel?: string | null;
  loadTheirsLabel?: string;
  onKeepMine: () => void;
  onLoadTheirs: () => void;
}) {
  // A live region says what changes in it, not what it is first drawn with. The
  // sentence and facts are drawn at once but reach assistive technology a moment
  // after the region, in a later task than the one that drew it (an effect can
  // run before the browser paints), so they are said when the notice appears,
  // and said again, once and whole, whenever they change.
  const [said, setSaid] = React.useState(false);
  React.useEffect(() => {
    const exposed = window.setTimeout(() => setSaid(true), 100);
    return () => window.clearTimeout(exposed);
  }, []);
  return (
    <div
      role="group"
      aria-label={`A newer version of ${object}`}
      className="grid gap-3 rounded-control bg-status-warning-surface p-3 text-ui text-status-warning-foreground"
    >
      <div aria-live="polite" aria-atomic="true">
        <div className="grid gap-3" aria-hidden={said ? undefined : true}>
          <p>
            {message ?? `Someone saved a newer version of ${object}. Your edits are kept here. Compare, then keep yours or load theirs.`}
          </p>
          {facts.length > 0 && (
            <dl className="grid gap-2">
              {facts.map(({ label, theirs }) => (
                <div key={label} className="grid gap-0.5">
                  <dt className="font-medium">Their {label.charAt(0).toLowerCase() + label.slice(1)}</dt>
                  <dd className="whitespace-pre-wrap [overflow-wrap:anywhere]">{theirs || "Empty"}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {keepMineLabel !== null && <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onKeepMine}>{keepMineLabel}</Button>}
        <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onLoadTheirs}>{loadTheirsLabel}</Button>
      </div>
    </div>
  );
}
